import { TabsComponent } from "@theme/tabs";
import { onDocumentReady, isDesktopBreakpoint, debounce } from "@theme/utilities";
import { DialogCloseEvent } from "@theme/events";

export class CollectionsWithTabs extends TabsComponent {
  #timeoutId = null;
  #hasAutoPlay = false;
  #autoInterval = 0;
  #remainingMs = 0;
  #runEnd = 0;
  #section = null;
  #tabsNav = null;
  #panelEventHandlers = [];
  #lastIsDesktop = false;
  #intersectionObserver = null;
  #isIntersecting = false;
  #boundHandleFocusIn = null;
  #pausedByPanel = false;
  #pausedByDialog = false;
  #boundHandleQuickAddClick = null;
  #boundHandleDialogClose = null;

  #handleResize = debounce(() => {
    // Only react to an actual breakpoint change, not every width tweak (zoom,
    // scrollbar, devtools, mobile address bar) — those shouldn't reset the tab.
    const isDesktop = isDesktopBreakpoint();
    if (isDesktop === this.#lastIsDesktop) return;
    this.#lastIsDesktop = isDesktop;

    this.#stopAutoRotation();
    this.#hasAutoPlay = this.hasAttribute('data-auto-interval');
    this.#autoInterval = parseInt(this.getAttribute('data-auto-interval'), 10);
    this.setSelectedIndex(0);
    this.#setColorScheme(0);
    if (this.#isIntersecting) this.#startAutoRotation();
  }, 100);

  connectedCallback() {
    // Set autoplay state before super so setupEventListeners (called inside super) can use it
    this.#hasAutoPlay = this.hasAttribute('data-auto-interval');
    this.#autoInterval = parseInt(this.getAttribute('data-auto-interval'), 10);
    this.#lastIsDesktop = isDesktopBreakpoint();
    super.connectedCallback();
    this.#section = this.classList.contains('section-content--boxed') ? this : this.closest('.collections-with-tabs-section');
    this.#tabsNav = Array.from(this.querySelectorAll('.tabs-nav__item'));
    this.#setColorScheme(0);
    this.#boundHandleFocusIn = this.#handleFocusIn.bind(this);
    this.addEventListener('focusin', this.#boundHandleFocusIn);
    this.#setupPanelHoverListeners();
    this.#setupDialogListeners();
    onDocumentReady(() => this.#setupIntersectionObserver());
    window.addEventListener('resize', this.#handleResize);
    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', (e) => this.#changeTabSelected(e));
    }
  }

  disconnectedCallback() {
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    if (this.#boundHandleQuickAddClick) {
      this.removeEventListener('click', this.#boundHandleQuickAddClick);
      this.#boundHandleQuickAddClick = null;
    }
    if (this.#boundHandleDialogClose) {
      document.removeEventListener(DialogCloseEvent.eventName, this.#boundHandleDialogClose);
      this.#boundHandleDialogClose = null;
    }
    window.removeEventListener('resize', this.#handleResize);
    if (this.#boundHandleFocusIn) {
      this.removeEventListener('focusin', this.#boundHandleFocusIn);
      this.#boundHandleFocusIn = null;
    }
    this.#handleResize.cancel?.();
    this.#removePanelEventListeners();
    this.#stopAutoRotation();
    super.disconnectedCallback();
  }

  #changeTabSelected(event) {
    const index = this.#tabsNav.indexOf(event.target);
    if (index >= 0) {
      this.#setColorScheme(index);
      this.setSelectedIndex(index);
    }
  }

  #handleFocusIn(event) {
    if (!(event.target instanceof Element)) return;
    const focusableTabContent = event.target.closest('.tabs-nav__item-content');
    if (focusableTabContent && this.contains(focusableTabContent)) {
      const tab = focusableTabContent.closest('.tabs-nav__item');
      const tabIndex = this.#tabsNav.indexOf(tab);
      if (tabIndex >= 0) {
        this.#activateTabByIndex(tabIndex);
      }
      return;
    }

    const bannerProductsContainer = event.target.closest('.banner__products');
    if (bannerProductsContainer && this.contains(event.target)) {
      const panel = bannerProductsContainer.closest('.tabs-content__item');
      const panelIndex = this.panels?.indexOf(panel);
      if (panelIndex >= 0) {
        this.#activateTabByIndex(panelIndex);
      }
    }
  }

  #activateTabByIndex(index) {
    if (index === this.selectedIndex) return;
    this.#setColorScheme(index);
    this.setSelectedIndex(index);
    if (this.#hasAutoPlay) {
      Promise.resolve().then(() => this.#restartProgress());
    }
  }

  // Panel hover listeners: only pause when the active panel contains .banner__products
  #setupPanelHoverListeners() {
    if (!this.#hasAutoPlay) return;
    this.panels?.forEach((panel) => {
      const onEnter = () => {
        if (!isDesktopBreakpoint()) return;
        // Check at hover time to handle lazy-loaded content
        if (!panel.querySelector('.banner__products')) return;
        this.#pauseForPanel();
      };
      const onLeave = () => {
        if (!isDesktopBreakpoint() || !this.#pausedByPanel) return;
        this.#resumeFromPanel();
      };
      panel.addEventListener('mouseenter', onEnter);
      panel.addEventListener('mouseleave', onLeave);
      this.#panelEventHandlers.push({ panel, onEnter, onLeave });
    });
  }

  #removePanelEventListeners() {
    this.#panelEventHandlers.forEach(({ panel, onEnter, onLeave }) => {
      panel.removeEventListener('mouseenter', onEnter);
      panel.removeEventListener('mouseleave', onLeave);
    });
    this.#panelEventHandlers = [];
  }

  onTabClick(e, index) {
    this.#setColorScheme(index);
    if (!this.#hasAutoPlay || index === this.selectedIndex) return;
    // Restart progress from beginning for the newly clicked tab
    Promise.resolve().then(() => this.#restartProgress());
  }

  // Tab item hover: restart progress only when switching to a different tab
  onTabHoverEnter(e, index) {
    if (isDesktopBreakpoint()) this.#setColorScheme(index);
    if (!this.#hasAutoPlay || !isDesktopBreakpoint()) return;
    if (index === this.selectedIndex) return;
    this.classList.remove('is-auto-paused');
    // Defer until after parent's setSelectedIndex updates btn--selected on the new tab
    Promise.resolve().then(() => this.#restartProgress());
  }

  get #prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // Restart CSS animation from 0 and reset the JS timer to full interval
  #restartProgress() {
    if (this.#prefersReducedMotion) return;
    // Toggle is-auto-rotating to force the @keyframes animation to restart on the selected tab
    this.classList.remove('is-auto-rotating');
    void this.offsetWidth; // force reflow so animation resets
    this.classList.add('is-auto-rotating');
    this.#runAutoRotation(this.#autoInterval);
  }

  #pauseAutoplay() {
    if (this.#timeoutId) {
      clearTimeout(this.#timeoutId);
      this.#timeoutId = null;
      this.#remainingMs = Math.max(0, this.#runEnd - Date.now());
    }
    this.classList.add('is-auto-paused');
  }

  #resumeAutoplay() {
    if (this.#pausedByPanel || this.#pausedByDialog) return;
    this.classList.remove('is-auto-paused');
    const ms = this.#remainingMs > 0 ? this.#remainingMs : this.#autoInterval;
    this.#runAutoRotation(ms);
  }

  #pauseForPanel() {
    this.#pausedByPanel = true;
    this.#pauseAutoplay();
  }

  // Resume from panel hover (no tab advance — user was just hovering content)
  #resumeFromPanel() {
    this.#pausedByPanel = false;
    this.#resumeAutoplay();
  }

  #pauseForDialog() {
    this.#pausedByDialog = true;
    this.#pauseAutoplay();
  }

  #resumeFromDialog() {
    if (!this.#pausedByDialog) return;
    // Another dialog (e.g. quick-add) may still be open — wait until none remain.
    if (document.querySelector('.dialog[open]')) return;
    this.#pausedByDialog = false;
    this.#resumeAutoplay();
  }

  #setupDialogListeners() {
    if (!this.#hasAutoPlay) return;
    this.#boundHandleQuickAddClick = (e) => {
      if (e.target.closest('.banner__product-quick-add')) this.#pauseForDialog();
    };
    this.addEventListener('click', this.#boundHandleQuickAddClick);
    // dialog:close is dispatched on the body-level dialog element, not inside this component
    this.#boundHandleDialogClose = this.#resumeFromDialog.bind(this);
    document.addEventListener(DialogCloseEvent.eventName, this.#boundHandleDialogClose);
  }

  #setupIntersectionObserver() {
    if (!this.#hasAutoPlay) return;
    this.#intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        this.#isIntersecting = entry.isIntersecting;
        if (entry.isIntersecting) {
          this.#startAutoRotation();
        } else {
          this.#stopAutoRotation();
        }
      },
      { threshold: 0.1 }
    );
    this.#intersectionObserver.observe(this);
  }

  #startAutoRotation() {
    if (!this.#hasAutoPlay || !this.tabs?.length) return;
    if (this.#prefersReducedMotion) return;
    const intervalMs = parseInt(this.getAttribute('data-auto-interval'), 10);
    if (intervalMs <= 0) return;
    this.style.setProperty('--auto-interval', `${intervalMs / 1000}`);
    this.classList.add('is-auto-rotating');
    this.#runAutoRotation(intervalMs);
  }

  #runAutoRotation(intervalMs) {
    // Guard against stacked timers (iOS background/foreground, rapid hover, resize)
    if (this.#timeoutId) {
      clearTimeout(this.#timeoutId);
      this.#timeoutId = null;
    }
    if (this.#prefersReducedMotion) return;
    if (!intervalMs || intervalMs <= 0) return;
    this.#remainingMs = intervalMs;
    this.#runEnd = Date.now() + intervalMs;
    // Add 100ms after CSS animation's nominal end so the bar visually reaches 100%
    // before the tab advances (setTimeout jitter can otherwise fire 50-100ms early)
    this.#timeoutId = setTimeout(() => {
      this.#timeoutId = null;
      if (!this.tabs?.length) return;
      const nextIndex = (this.selectedIndex + 1) % this.tabs.length;
      this.setSelectedIndex(nextIndex);
      this.#setColorScheme(nextIndex);
      this.#runAutoRotation(this.#autoInterval);
    }, intervalMs + 100);
  }

  #stopAutoRotation() {
    if (this.#timeoutId) {
      clearTimeout(this.#timeoutId);
      this.#timeoutId = null;
    }
    this.#remainingMs = 0;
    this.#runEnd = 0;
    this.#pausedByPanel = false;
    this.#pausedByDialog = false;
    this.classList.remove('is-auto-rotating', 'is-auto-paused');
  }

  #setColorScheme(indexOrTarget) {
    const target = typeof indexOrTarget === 'number' ? this.#tabsNav[indexOrTarget] : indexOrTarget;
    if (!this.#section || !target?.dataset?.colorScheme) return;
    const customBackground = target.dataset.customBackground;
    if (customBackground) {
      this.#section.style.setProperty('--color-background', customBackground);
    } else {
      this.#section.style.removeProperty('--color-background');
    }
    const colorScheme = target.dataset.colorScheme;
    [...this.#section.classList].filter((c) => c.includes('color-')).forEach((c) => this.#section.classList.remove(c));
    this.#section.classList.add(colorScheme);
  }
}

if (!customElements.get('collections-with-tabs')) {
  customElements.define('collections-with-tabs', CollectionsWithTabs);
}
